/* eslint-disable no-useless-escape --
 * La barra invertida de `<\/script>` es deliberada: la secuencia </script> sin
 * escapar cerraria el bloque antes de tiempo si este archivo quedara incrustado
 * en una pagina. En un literal de plantilla `\/` produce `/`, asi que el HTML
 * generado es identico.
 */

import { ESTILOS_VISTA_PREVIA, barraVistaPrevia, abrirVistaPrevia } from './vistaPrevia';

/**
 * El comprobante de una venta, en el formato que va a necesitar la factura
 * electrónica.
 *
 * Un solo documento, un solo ancho: **80 mm**, que es lo que hay en el
 * mostrador de una ferretería. No existe versión A4 a propósito. Antes
 * convivían dos plantillas que imprimían la misma venta con datos diferentes
 * —el ticket no decía el timbrado ni desglosaba el IVA, la factura A4 no
 * mostraba los datos del pago—, y mantener las dos significaba agregar cada
 * dato nuevo dos veces y que tarde o temprano una quedara atrás. Que es
 * exactamente lo que había pasado.
 *
 * Si algún día hace falta imprimir en hoja, el lugar es acá y es una hoja de
 * estilos más; mientras no haga falta, no está.
 *
 * La estructura sigue la del KuDE, la representación gráfica que SIFEN exige
 * de un documento electrónico. Hoy se imprime con timbrado autoimpresor, pero
 * el día que se conecte el facturador no hay que rehacer nada: lo único que
 * cambia es que el recuadro del CDC deja de estar vacío.
 */

const TIPOS_DOCUMENTO = {
    ruc: 'RUC',
    ci: 'C.I.',
    pasaporte: 'Pasaporte',
    extranjero: 'Doc. extranjero',
    innominado: 'Sin identificar',
};

const TITULOS = {
    factura:      ['FACTURA',          'FACTURA ELECTRÓNICA'],
    nota_credito: ['NOTA DE CRÉDITO',  'NOTA DE CRÉDITO ELECTRÓNICA'],
    nota_debito:  ['NOTA DE DÉBITO',   'NOTA DE DÉBITO ELECTRÓNICA'],
    recibo:       ['RECIBO',           'RECIBO ELECTRÓNICO'],
};

// Cómo se rotula cada dato del pago. La tarjeta se enmascara: un comprobante
// que se tira a la basura no puede llevar el número completo.
const ETIQUETAS_PAGO = {
    banco_origen: 'Banco origen',
    nro_cuenta: 'Nro. cuenta',
    id_transaccion: 'Comprobante',
    nro_tarjeta: 'Tarjeta',
    banco: 'Banco',
    procesadora: 'Red',
    entidad: 'Entidad',
};

const escapar = (texto) => String(texto ?? '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const numero = (n) => Math.round(parseFloat(n) || 0).toLocaleString('es-PY');

const fecha = (valor) => (valor ? new Date(valor).toLocaleDateString('es-PY') : '—');

const hora = (valor) =>
    (valor ? new Date(valor).toLocaleTimeString('es-PY', { hour12: false }) : '—');

function titulo(documento) {
    // Sin documento fiscal esto es el detalle de una venta, no una factura.
    // Titularlo «FACTURA» sería hacerlo pasar por lo que no es: el comprobante
    // sale impreso, el cliente se lo lleva, y nadie emitió nada ante el fisco.
    if (!documento) return 'COMPROBANTE DE VENTA';

    const [simple, electronico] = TITULOS[documento.tipo] || TITULOS.factura;
    // Un documento electrónico trae identificador externo —el CDC—, aunque
    // todavía esté vacío mientras no se envíe. Un comprobante autoimpresor ni
    // siquiera tiene ese campo.
    return documento.identificador_externo !== undefined ? electronico : simple;
}

/**
 * Cómo se describe el receptor.
 *
 * Una venta sin cliente no es un dato faltante: es una venta a consumidor
 * final sin identificar, y el comprobante lo declara así. Poner una raya en su
 * lugar deja la duda de si se olvidaron de cargarlo.
 */
function receptorDe(venta) {
    const r = venta.receptor;
    if (r && !r.identificado) {
        return { nombre: 'Consumidor final', tipo: 'Sin identificar', documento: '—',
                 direccion: '', telefono: '' };
    }
    if (r) {
        return {
            nombre: r.nombre || '—',
            tipo: TIPOS_DOCUMENTO[r.tipo_documento] || 'Documento',
            documento: r.documento_completo || '—',
            direccion: r.direccion || '',
            telefono: r.telefono || '',
        };
    }
    // Venta vieja, de antes de que el servidor armara el receptor.
    return {
        nombre: venta.cliente_nombre || 'Consumidor final',
        tipo: venta.cliente_ruc ? 'RUC / C.I.' : 'Sin identificar',
        documento: venta.cliente_ruc || '—',
        direccion: '',
        telefono: '',
    };
}

/** Contado, o crédito con sus condiciones. */
function condicionDe(venta) {
    if (venta.condicion_venta !== 'credito') return 'CONTADO';
    const partes = [];
    if (venta.cuotas > 0) partes.push(`${venta.cuotas} cuota${venta.cuotas !== 1 ? 's' : ''}`);
    if (venta.plazo_dias > 0) partes.push(`${venta.plazo_dias} días`);
    return partes.length ? `CRÉDITO — ${partes.join(', ')}` : 'CRÉDITO';
}

/**
 * La liquidación del IVA, tasa por tasa.
 *
 * Se listan solo las tasas que la venta tiene. Antes se imprimían además filas
 * fijas de «IVA 5% → 0» y «Exento → 0», así que una venta con artículos al 5%
 * mostraba dos renglones del 5%: el real y el fijo en cero.
 */
function liquidacionDe(venta) {
    const total = parseFloat(venta.total) || 0;
    const desglose = venta.desglose_iva?.length
        ? venta.desglose_iva
        : [{ tasa: 10, gravado: String(total - Math.round(total * 10 / 110)),
             iva: String(Math.round(total * 10 / 110)) }];

    // Cada tasa en una fila con sus dos importes: la base gravada y el
    // impuesto que le corresponde. Es como lo muestra una factura real, y se
    // lee mejor que dos renglones separados por tasa.
    const lineas = desglose.map(g => (Number(g.tasa) === 0
        ? ['Exentas E  :', g.gravado, 0]
        : [`Gravado ${g.tasa}% :`, g.gravado, g.iva]));

    const totalIva = desglose.reduce((suma, g) => suma + (parseFloat(g.iva) || 0), 0);
    lineas.push(['Total IVA', totalIva]);
    return lineas;
}

/** Los datos del pago que valga la pena imprimir. */
function datosPagoDe(venta) {
    const dp = venta.datos_pago;
    if (!dp || typeof dp !== 'object') return [];
    return Object.entries(dp)
        .filter(([, valor]) => valor)
        .map(([clave, valor]) => [
            ETIQUETAS_PAGO[clave] || clave,
            clave === 'nro_tarjeta' ? `****${String(valor).slice(-4)}` : valor,
        ]);
}

function emisorDe(settings) {
    return {
        nombre: settings?.nombre_negocio || 'Mi Ferretería',
        fantasia: settings?.nombre_fantasia || '',
        ruc: settings?.ruc || '',
        direccion: settings?.direccion || '',
        ciudad: settings?.ciudad || '',
        departamento: settings?.departamento || '',
        distrito: settings?.distrito || '',
        telefono: settings?.telefono || '',
        // El negocio puede tener varias actividades inscriptas y el
        // comprobante las declara todas, así que se imprimen en una línea
        // separadas por punto y coma, como salen en una factura real.
        actividades: (settings?.actividades_economicas || [])
            .filter(a => a?.descripcion || a?.codigo)
            .map(a => (a.codigo ? `${a.descripcion} (${a.codigo})` : a.descripcion)),
        logo: settings?.logo_url || '',
        mensaje: settings?.mensaje_ticket || '¡Gracias por su compra!',
    };
}

// ── El cuerpo del comprobante ───────────────────────────────────────────────
//
// Sigue el formato de una factura electronica paraguaya real, tomada de una
// emitida por un comercio del rubro. No es copiar por copiar: ese formato es el
// que el cliente reconoce, el que el contador sabe leer y el que la DNIT espera
// ver en la representacion grafica de un documento electronico.
//
// Lo que ese formato tiene y conviene respetar:
//
//  - Cabecera centrada con razon social, RUC, direccion, sucursal, telefono, y
//    el timbrado con su vigencia completa: inicio y fin, no solo el fin.
//  - Receptor con su documento, y TIPO FACTURA (contado o credito) separado de
//    COND. PAGO (con que se pago). Son dos datos distintos y se confunden
//    facil: una venta al contado puede pagarse con tarjeta.
//  - LOCAL, CAJA y numero de transaccion, que es como se ubica una operacion
//    cuando hay que buscarla meses despues.
//  - La tasa de IVA al final de cada linea, como un numero suelto.
//  - La liquidacion en tres columnas: base gravada por tasa y su impuesto.
//  - Recuento de items y de articulos vendidos, y quien atendio.
//  - El CDC en grupos de cuatro, con la direccion de consulta de la DNIT y la
//    leyenda de representacion grafica.

const ESTILOS_80MM = `
  * { margin:0; padding:0; box-sizing:border-box; }
  body { font-family: 'Courier New', Courier, monospace; font-size: 11px; line-height: 1.3;
         color: #000; width: 80mm; margin: 0 auto; padding: 4mm 3mm; }
  .centro { text-align: center; }
  .negrita { font-weight: bold; }
  .chico { font-size: 10px; }
  .mini { font-size: 9px; }
  .regla { border-top: 1px solid #000; margin: 4px 0; }
  .regla-punteada { border-top: 1px dashed #000; margin: 4px 0; }

  /* Las lineas de datos usan etiqueta de ancho fijo para que los dos puntos
     queden alineados, como en una factura impresa por caja. */
  .dato { display: flex; }
  .dato .et { flex: 0 0 14ch; }
  .dato .va { flex: 1; word-break: break-word; }

  table { width: 100%; border-collapse: collapse; font-size: 10px; }
  td { padding: 0; vertical-align: top; }
  .der { text-align: right; }

  /* El nombre del articulo ocupa su propia linea: en 80 mm no entra al lado de
     cantidad, precio e importe sin cortarse, y un nombre cortado no sirve para
     reclamar nada. */
  .art-cabecera td { padding-top: 3px; }

  .liquidacion td { padding: 1px 0; }
  .total-linea { font-size: 13px; font-weight: bold; }

  .qr-hueco { width: 38mm; height: 38mm; margin: 8px auto; border: 1px dashed #999;
              display: flex; align-items: center; justify-content: center;
              font-size: 8px; color: #777; text-align: center; padding: 4px; }
  /* El aviso de prueba va recuadrado y arriba, donde se mira. Una leyenda al
     pie se lee cuando ya se entrego el papel. */
  .aviso-prueba { border: 2px solid #000; padding: 3px 2px; margin: 4px 0 2px;
                  font-weight: bold; font-size: 12px; line-height: 1.2; }
  .aviso-prueba-nota { font-weight: normal; margin-top: 2px; }

  .cdc { font-size: 10px; letter-spacing: .5px; word-break: break-word; margin-top: 2px; }
  .leyenda { font-size: 8px; color: #333; margin-top: 6px; text-align: center; line-height: 1.25; }

  @media print { @page { margin: 0; size: 80mm auto; } body { padding: 2mm 3mm; } }
`;

/** El CDC se lee en grupos de cuatro: 44 digitos seguidos no los copia nadie. */
function cdcEnGrupos(cdc) {
    return String(cdc).replace(/(.{4})/g, '$1 ').trim();
}

function cuerpo80mm({ emisor, receptor, venta, documento, liquidacion, pagos, moneda, cdc,
                      esElectronico, esPrueba }) {
    const detalles = venta.detalles || [];
    const unidades = detalles.reduce((suma, d) => suma + (Number(d.cantidad) || 0), 0);
    const nombreTipo = titulo(documento).split(' ')[0];

    const filas = detalles.map(d => `
    <tr class="art-cabecera">
      <td colspan="3">${escapar(d.producto_codigo || '')} ${escapar(d.producto_nombre || '—')}</td>
    </tr>
    <tr>
      <td>${d.cantidad} x ${numero(d.precio_unitario)}</td>
      <td class="der">${moneda} ${numero(d.subtotal)}</td>
      <td class="der" style="width:3ch;">${d.tasa_iva === 0 ? 'E' : (d.tasa_iva ?? 10)}</td>
    </tr>`).join('');

    // La liquidacion sale agrupada por tasa, en tres columnas: cada tasa con su
    // base gravada y su impuesto, como en la factura de referencia.
    const porTasa = liquidacion.filter(([etiqueta]) => etiqueta !== 'Total IVA');
    const filaTotalIva = liquidacion.find(([etiqueta]) => etiqueta === 'Total IVA');
    const totalIva = filaTotalIva ? filaTotalIva[1] : 0;

    return `
<div class="centro">
  ${emisor.logo ? `<img src="${escapar(emisor.logo)}" alt="" style="max-height:44px;margin-bottom:3px;"><br>` : ''}
  <div class="negrita">${escapar(emisor.nombre)}</div>
  ${emisor.ruc ? `<div>R.U.C. ${escapar(emisor.ruc)}</div>` : ''}
  ${emisor.direccion ? `<div class="chico">${escapar(emisor.direccion)}</div>` : ''}
  ${emisor.fantasia ? `<div class="chico">${escapar(emisor.fantasia)}</div>` : ''}
  ${(emisor.distrito || emisor.departamento)
    ? `<div class="chico">${escapar([emisor.distrito, emisor.departamento].filter(Boolean).join(' - '))}</div>` : ''}
  ${emisor.telefono ? `<div class="chico">Tel.: ${escapar(emisor.telefono)}</div>` : ''}
  ${emisor.actividades.length
    ? `<div class="mini">${escapar(emisor.actividades.join('; '))}</div>`
    : ''}
  ${documento ? (esPrueba ? `
    <div class="aviso-prueba">
      DOCUMENTO DE PRUEBA<br>SIN VALIDEZ FISCAL
      <div class="mini aviso-prueba-nota">No sustituye al comprobante legal del negocio</div>
    </div>
    <div class="negrita" style="margin-top:2px;">${nombreTipo} — Nro. interno : ${escapar(documento.numero_completo || '—')}</div>
  ` : `
    <div class="chico" style="margin-top:3px;">TIMBRADO Nro.: ${escapar(documento.timbrado || '—')}</div>
    ${documento.fecha_inicio_timbrado
      ? `<div class="chico">Fecha Inicio : ${fecha(documento.fecha_inicio_timbrado)}</div>` : ''}
    ${documento.fecha_vencimiento_timbrado
      ? `<div class="chico">Fecha Fin&nbsp;&nbsp;&nbsp;&nbsp;: ${fecha(documento.fecha_vencimiento_timbrado)}</div>` : ''}
    <div class="negrita" style="margin-top:2px;">${nombreTipo} Nro. : ${escapar(documento.numero_completo || '—')}</div>
  `) : `
    <div class="negrita" style="margin-top:3px;">${titulo(documento)}</div>
    <div class="chico">Venta interna Nro. ${venta.id}</div>
    <div class="negrita chico">SIN VALIDEZ FISCAL</div>
  `}
</div>

<div class="regla-punteada"></div>

<div class="chico">
  <div class="dato"><span class="et">CLIENTE</span><span class="va">: ${escapar(receptor.nombre)}</span></div>
  <div class="dato"><span class="et">${escapar(receptor.tipo)}</span><span class="va">: ${escapar(receptor.documento)}</span></div>
  ${receptor.direccion
    ? `<div class="dato"><span class="et">DIRECCION</span><span class="va">: ${escapar(receptor.direccion)}</span></div>` : ''}
  ${receptor.telefono
    ? `<div class="dato"><span class="et">TELEFONO</span><span class="va">: ${escapar(receptor.telefono)}</span></div>` : ''}
  <div class="dato"><span class="et">TIPO ${nombreTipo}</span><span class="va">: ${condicionDe(venta)}</span></div>
  ${venta.forma_pago
    ? `<div class="dato"><span class="et">COND. PAGO</span><span class="va">: ${escapar(venta.forma_pago.toUpperCase())}</span></div>` : ''}
  <div>FECHA EMISION: ${fecha(venta.fecha)}&nbsp;&nbsp;HORA: ${hora(venta.fecha)}</div>
  ${documento
    ? `<div>LOCAL: ${escapar(documento.numero_establecimiento || '—')}&nbsp;&nbsp;CAJA: ${escapar(documento.numero_punto_expedicion || '—')}&nbsp;&nbsp;TRANSACCION Nro.: ${venta.id}</div>`
    : `<div>TRANSACCION Nro.: ${venta.id}</div>`}
</div>

<div class="regla"></div>

<div class="mini">CODIGO / DESC. ARTICULO</div>
<div class="mini">CANT. x PRECIO UNITARIO &nbsp;&nbsp;&nbsp;&nbsp; IMPORTE &nbsp;T.IVA</div>
<div class="regla-punteada"></div>

<table><tbody>${filas}</tbody></table>

<div class="regla-punteada"></div>

<table><tbody>
  <tr class="total-linea"><td>TOTAL:</td><td class="der">${moneda} ${numero(venta.total)}</td></tr>
</tbody></table>

${venta.forma_pago || pagos.length ? `
<div class="chico" style="margin-top:4px;">DETALLE DE PAGOS:</div>
<table class="chico"><tbody>
  ${venta.forma_pago
    ? `<tr><td>${escapar(venta.forma_pago.toUpperCase())}</td><td class="der">${moneda} ${numero(venta.total)}</td></tr>`
    : ''}
  ${pagos.map(([etiqueta, valor]) =>
    `<tr><td class="mini">${escapar(etiqueta)}</td><td class="der mini">${escapar(valor)}</td></tr>`).join('')}
  <tr class="negrita"><td>TOTAL PAGOS</td><td class="der">${moneda} ${numero(venta.total)}</td></tr>
</tbody></table>` : ''}

<div class="regla-punteada"></div>

<table class="liquidacion chico"><tbody>
  <tr class="mini"><td>SUB TOTALES</td><td class="der">LIQUIDACION</td><td class="der">IVA</td></tr>
  ${porTasa.map(([etiqueta, base, impuesto]) =>
    `<tr><td>${etiqueta}</td><td class="der">${numero(base)}</td><td class="der">${impuesto == null ? '' : numero(impuesto)}</td></tr>`).join('')}
  <tr class="negrita"><td></td><td class="der">TOTAL IVA:</td><td class="der">${numero(totalIva)}</td></tr>
</tbody></table>

<div class="regla-punteada"></div>

<div class="chico">
  <div>Total items: ${detalles.length}</div>
  <div>Total articulos vendidos: ${unidades}</div>
  ${venta.vendedor_nombre ? `<div>ATENDIDO POR: ${escapar(venta.vendedor_nombre.toUpperCase())}</div>` : ''}
</div>

${(() => {
  // Cuatro situaciones distintas, y confundirlas desinforma:
  //  - sin documento fiscal: la venta todavia no se facturo;
  //  - documento emitido en modo prueba: no es comprobante de nada;
  //  - documento electronico aceptado: lleva su CDC y su codigo de consulta;
  //  - documento electronico sin enviar: el CDC esta pendiente, y decir
  //    «autoimpresor» en su lugar afirmaria una modalidad que no es la suya.
  if (!documento) {
    return `
<div class="regla-punteada"></div>
<div class="leyenda">Este documento no tiene validez fiscal.<br>
La factura se emite desde Historial de Ventas.</div>`;
  }
  // Antes que el CDC a proposito. Si un documento de prueba llegara a tener
  // identificador, imprimir el codigo de consulta de la DNIT lo presentaria
  // como un documento electronico valido, que es justo lo que no es.
  if (esPrueba) {
    return `
<div class="regla-punteada"></div>
<div class="leyenda">DOCUMENTO DE PRUEBA, SIN VALIDEZ FISCAL.<br>
Emitido por un sistema en evaluación. No sustituye al comprobante<br>
legal del negocio ni acredita la operación ante la administración tributaria.</div>`;
  }
  if (cdc) {
    const qr = documento.respuesta_externa && documento.respuesta_externa.qr;
    return `
<div class="qr-hueco">${qr
      ? `<img src="${escapar(qr)}" alt="QR de la factura" style="width:100%;height:100%;">`
      : 'Código QR<br>lo provee el facturador'}</div>
<div class="leyenda">
  Consulta la validez de esta Factura Electrónica con<br>
  el número de CDC impreso abajo en:<br>
  https://ekuatia.set.gov.py/consultas/
</div>
<div class="cdc centro">${escapar(cdcEnGrupos(cdc))}</div>
<div class="leyenda">
  ESTE DOCUMENTO ES UNA REPRESENTACION<br>
  GRAFICA DE UN DOCUMENTO ELECTRONICO (XML)
</div>`;
  }
  if (esElectronico) {
    return `
<div class="regla-punteada"></div>
<div class="leyenda">CDC pendiente de asignación.<br>
El documento todavía no fue aceptado por la administración tributaria.</div>`;
  }
  return `
<div class="regla-punteada"></div>
<div class="leyenda">Comprobante con timbrado autoimpresor.</div>`;
})()}

<div class="regla-punteada"></div>
<div class="leyenda">
  Por disposición de la DNIT según Decreto 312/18 se debe identificar al
  adquirente de la compra; por lo tanto, no se permite emitir notas de crédito
  innominadas (sin nombre). Favor mencionar el número de C.I. o RUC por cada
  compra.
</div>

<div class="centro negrita chico" style="margin-top:6px;">** ${escapar(emisor.mensaje)} **</div>`;
}

// ── Armado ──────────────────────────────────────────────────────────────────

/**
 * @param {object} opciones
 * @param {boolean} [opciones.conBarra=true] La barra de vista previa. Se apaga
 *   cuando el documento se muestra embebido en la pantalla: ahí los botones
 *   los pone la aplicación y repetirlos adentro sería confuso.
 */
export function buildComprobanteHtml(venta, documento, settings, { conBarra = true } = {}) {
    const datos = {
        emisor: emisorDe(settings),
        receptor: receptorDe(venta),
        venta,
        documento,
        liquidacion: liquidacionDe(venta),
        pagos: datosPagoDe(venta),
        moneda: settings?.moneda || 'Gs.',
        cdc: documento?.identificador_externo || '',
        esElectronico: documento?.identificador_externo !== undefined,
        // Se lee del documento, que lo congeló al emitirse, y no de los ajustes
        // del negocio: el ajuste se va a apagar el día que empiece a facturar
        // de verdad, y los comprobantes del período de prueba no pueden pasar a
        // imprimirse como válidos por eso.
        //
        // La comparación es contra `false` y no un `!!` a propósito. Si el dato
        // no viniera —un backend sin migrar, una respuesta incompleta—, lo
        // seguro es tratarlo como prueba: equivocarse marcando de más no le
        // cuesta nada a nadie, y equivocarse al revés imprime un comprobante
        // que afirma una validez que no tiene.
        esPrueba: documento ? documento.emitido_en_prueba !== false : true,
    };

    const rotulo = `${titulo(documento)} — ${documento?.numero_completo || `venta #${venta.id}`}`;

    return `<!DOCTYPE html>
<html lang="es">
<head>
<meta charset="utf-8">
<title>${escapar(rotulo)}</title>
<style>
${ESTILOS_80MM}
${conBarra ? ESTILOS_VISTA_PREVIA : ''}
</style>
</head>
<body>
${conBarra ? barraVistaPrevia(escapar(rotulo)) : ''}
${cuerpo80mm(datos)}
</body>
</html>`;
}

export function openComprobante(venta, documento, settings) {
    return abrirVistaPrevia(
        buildComprobanteHtml(venta, documento, settings),
        { ancho: 420, alto: 760 },
    );
}
